<template>
  <div class="size-chart relative lg:flex">
    <div v-for="type in Object.keys(currentSizeChart)" :key="type" class="box mb-0 lg:flex-grow">
      <p class="ota-subtitle text-gray-600 flex align-middle my-6 lg:mt-3">
        <OSvg svg="measure" class="mr-3 w-9 h-9" />
        <span v-t="`SizeChart.Table.Titles.${capitalize(type)}`" class="leading-relaxed" />
      </p>
      <hr class="h-px bg-gray-200 border-none mb-6">
      <table class="table table-auto w-full text-center">
        <thead class="ota-small">
          <tr>
            <th v-t="'SizeChart.Table.Titles.Size'" />
            <th v-if="currentSizeChart[type][0].jeans" v-t="'SizeChart.Table.Titles.Jeans'" />
            <th v-t="type === 'men' ? 'SizeChart.Table.Titles.Waist' : 'SizeChart.Table.Titles.Age'" />
            <th v-if="locale === 'es' && type === 'boys'" v-text="'Talla ES'" />
            <th v-if="currentSizeChart[type][0].waistInch" v-t="'SizeChart.Table.Titles.WaistInch'" />
          </tr>
        </thead>
        <tbody class="ota-small-light">
          <tr v-for="(row, index) in currentSizeChart[type]" :key="index" class="rounded">
            <td v-text="row.size" />
            <td v-if="row.jeans" v-text="row.jeans" />
            <td v-text="getWaistValue(row)" />
            <td v-if="locale === 'es' && type === 'boys'" v-text="row.age" />
            <td v-if="row.waistInch" v-text="row.waistInch" />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { capitalize } from '@/utils/general'
import { Locales } from '@/enums/general'
const OSvg = () => import('@/components/OSvg')

export default {
  name: 'SizeChart',
  layout: 'signup-men',
  components: {
    OSvg
  },
  data () {
    const sizeChart = {}

    sizeChart[Locales.nl] = sizeChart[Locales.de] = {
      men: [
        { size: 'XS', jeans: '28', waist: '73-78' },
        { size: 'S', jeans: '29', waist: '79-84' },
        { size: 'M', jeans: '30/31', waist: '85-90' },
        { size: 'L', jeans: '32/33', waist: '91-96' },
        { size: 'XL', jeans: '34/36', waist: '97-102' },
        { size: 'XXL', jeans: '38/40', waist: '103-108' },
        { size: '3XL', jeans: '42', waist: '109-111' }
      ],
      boys: [
        { size: '104', age: '3-4' },
        { size: '110-116', age: '5-6' },
        { size: '122-128', age: '7-8' },
        { size: '134-140', age: '9-10' },
        { size: '146-152', age: '11-12' }
      ]
    }

    sizeChart[Locales.es] = sizeChart[Locales.it] = {
      men: [
        { size: 'XS', jeans: '28', waist: '73-78' },
        { size: 'S', jeans: '29', waist: '79-84' },
        { size: 'M', jeans: '30/31', waist: '85-90' },
        { size: 'L', jeans: '32/33', waist: '91-96' },
        { size: 'XL', jeans: '34/36', waist: '97-102' },
        { size: 'XXL', jeans: '38/40', waist: '103-108' },
        { size: '3XL', jeans: '42', waist: '109-111' }
      ],
      boys: [
        { size: '104', age: '4A' },
        { size: '110-116', age: '5A-6A' },
        { size: '122-128', age: '7A-8A' },
        { size: '134-140', age: '9A-10A' },
        { size: '146-152', age: '11A-12A' }
      ]
    }

    sizeChart[Locales.en] = {
      men: [
        { size: 'XS', jeans: '28', waist: '26-28 inch' },
        { size: 'S', jeans: '29', waist: '28-30 inch' },
        { size: 'M', jeans: '30/31', waist: '30-32 inch' },
        { size: 'L', jeans: '32/33', waist: '32-34 inch' },
        { size: 'XL', jeans: '34/36', waist: '34-36 inch' },
        { size: 'XXL', jeans: '38/40', waist: '36-38 inch' },
        { size: '3XL', jeans: '42', waist: '38-40 inch' }
      ],
      boys: [
        { size: '104', age: '3-4' },
        { size: '110-116', age: '5-6' },
        { size: '122-128', age: '7-8' },
        { size: '134-140', age: '9-10' },
        { size: '146-152', age: '11-12' }
      ]
    }

    sizeChart[Locales.fr] = {
      men: [
        { size: 'XS', jeans: '28', waist: '73-78' },
        { size: 'S', jeans: '29', waist: '79-84' },
        { size: 'M', jeans: '30/31', waist: '85-90' },
        { size: 'L', jeans: '32/33', waist: '91-96' },
        { size: 'XL', jeans: '34/36', waist: '97-102' },
        { size: 'XXL', jeans: '38/40', waist: '103-108' },
        { size: '3XL', jeans: '42', waist: '109-111' }
      ],
      boys: [
        { size: '104', age: '3A-4A' },
        { size: '110-116', age: '4A-6A' },
        { size: '122-128', age: '6A-8A' },
        { size: '134-140', age: '8A-10A' },
        { size: '146-152', age: '10A-12A' }
      ]
    }

    sizeChart[Locales.sv] = sizeChart[Locales.da] = sizeChart[Locales.cs] = sizeChart[Locales.no] = sizeChart[Locales.it] = sizeChart[Locales.pl] = sizeChart[Locales.pt] = sizeChart[Locales.fi] = {
      men: [
        { size: 'XS', jeans: '28', waist: '73-78' },
        { size: 'S', jeans: '29', waist: '79-84' },
        { size: 'M', jeans: '30/31', waist: '85-90' },
        { size: 'L', jeans: '32/33', waist: '91-96' },
        { size: 'XL', jeans: '34/36', waist: '97-102' },
        { size: 'XXL', jeans: '38/40', waist: '103-108' },
        { size: '3XL', jeans: '42', waist: '109-111' }
      ],
      boys: [
        { size: '104', age: '3-4' },
        { size: '110-116', age: '5-6' },
        { size: '122-128', age: '7-8' },
        { size: '134-140', age: '9-10' },
        { size: '146-152', age: '11-12' }
      ]
    }

    return {
      sizeChart,
      capitalize
    }
  },
  computed: {
    currentSizeChart () {
      return this.sizeChart[this.locale]
    },
    ...mapState('app', {
      locale: state => state.locale
    })
  },
  methods: {
    getWaistValue (row) {
      return row.waist || (this.locale === 'es' && row.age ? row.age.replace(/A/g, '') : row.age)
    }
  }
}
</script>

<style lang="postcss" scoped>
.size-chart {
  .table {
    &:last-of-type {
      @apply mb-0;
    }

    thead {
      th {
        @apply p-3;
      }
    }

    tbody {
      tr:nth-child(odd) {
        @apply bg-gray-100;
      }

      td {
        @apply p-4;
      }
    }
  }
}

@screen lg {
  .size-chart {
    .box {
      @apply mr-15;

      &:last-of-type {
        @apply mr-0;
      }
    }
  }
}
</style>
